import { useCallback, useEffect, useRef } from 'react';

type SoundType = 'questComplete' | 'cardReveal' | 'pointsUp' | 'chime' | 'stamp' | 'powerup' | 'hudSync' | 'transitionImpact';

interface AudioContextWithState extends AudioContext {
  state: AudioContextState;
}

export const useSoundEffects = () => {
  const audioContextRef = useRef<AudioContextWithState | null>(null);
  const isEnabledRef = useRef(true);

  useEffect(() => {
    try {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (e) {
      // Will init on user interaction
    }

    return () => {
      audioContextRef.current?.close();
    };
  }, []);

  const playTone = useCallback((
    ctx: AudioContext,
    frequency: number,
    startTime: number,
    duration: number,
    gain: number,
    type: OscillatorType = 'sine',
    fadeOut: boolean = true
  ) => {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, startTime);

    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(gain, startTime + 0.02);
    
    if (fadeOut) {
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    } else {
      gainNode.gain.setValueAtTime(gain, startTime + duration - 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    }

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + duration + 0.05);
  }, []);

  // Play a tone with frequency sweep for more dramatic effects
  const playFrequencySweep = useCallback((
    ctx: AudioContext,
    startFreq: number,
    endFreq: number,
    startTime: number,
    duration: number,
    gain: number,
    type: OscillatorType = 'sine'
  ) => {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(startFreq, startTime);
    oscillator.frequency.exponentialRampToValueAtTime(endFreq, startTime + duration);

    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(gain, startTime + 0.08);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + duration + 0.05);
  }, []);

  const playSound = useCallback((type: SoundType) => {
    if (!isEnabledRef.current || !audioContextRef.current) return;

    const ctx = audioContextRef.current;
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const time = ctx.currentTime;

    switch (type) {
      case 'hudSync': {
        // Futuristic boot-up / syncing sound for HUD transition
        const now = time;
        
        // Layer 1: Sub-bass pressure buildup (felt, not heard as tone)
        playFrequencySweep(ctx, 20, 60, now, 1.2, 0.06, 'sine');
        
        // Layer 2: Digital data stream - rapid subtle clicks
        for (let i = 0; i < 12; i++) {
          const freq = 1200 + (i % 3) * 200;
          playTone(ctx, freq, now + 0.1 + i * 0.08, 0.03, 0.02, 'sine');
        }
        
        // Layer 3: Scanning sweep (low to high)
        playFrequencySweep(ctx, 100, 2000, now + 0.2, 0.8, 0.04, 'triangle');
        
        // Layer 4: Electronic hum bed
        playTone(ctx, 110, now + 0.1, 1.4, 0.05, 'triangle');
        playTone(ctx, 165, now + 0.15, 1.3, 0.03, 'sine');
        
        // Layer 5: Power-up whine
        playFrequencySweep(ctx, 400, 1200, now + 0.6, 0.6, 0.05, 'sine');
        
        // Layer 6: Climax ping at end
        playTone(ctx, 880, now + 1.1, 0.3, 0.06, 'sine');
        playTone(ctx, 1320, now + 1.15, 0.25, 0.04, 'sine');
        break;
      }

      case 'questComplete': {
        // FUTURISTIC SCI-FI ACHIEVEMENT SOUND - smooth attack, no "toing"
        const now = time;
        const offset = 0.3; // 300ms of silence before anything audible
        
        // Layer 1: Inaudible sub pressure (15-20Hz - below hearing threshold)
        playFrequencySweep(ctx, 15, 35, now + offset, 0.5, 0.04, 'sine');
        
        // Layer 2: Smooth pad rise with longer fade-in
        playFrequencySweep(ctx, 60, 140, now + offset + 0.2, 0.6, 0.08, 'sine');
        playFrequencySweep(ctx, 100, 220, now + offset + 0.25, 0.55, 0.06, 'triangle');
        
        // Layer 3: Rising digital sweep - triangle instead of sawtooth (smoother)
        playFrequencySweep(ctx, 180, 1600, now + offset + 0.4, 0.5, 0.04, 'triangle');
        playFrequencySweep(ctx, 250, 2000, now + offset + 0.45, 0.45, 0.03, 'sine');
        
        // Layer 4: Impact at climax - sine/triangle for smooth attack
        playTone(ctx, 1200, now + offset + 0.75, 0.2, 0.09, 'sine');
        playTone(ctx, 1600, now + offset + 0.77, 0.18, 0.07, 'triangle');
        playTone(ctx, 2200, now + offset + 0.79, 0.15, 0.05, 'sine');
        
        // Layer 5: Deep bass hit with smooth envelope
        playTone(ctx, 60, now + offset + 0.75, 0.5, 0.12, 'sine');
        playTone(ctx, 120, now + offset + 0.77, 0.45, 0.08, 'triangle');
        
        // Layer 6: Crystalline chimes cascade
        const chimes = [880, 1100, 1320, 1760, 2200, 2640];
        chimes.forEach((freq, i) => {
          playTone(ctx, freq, now + offset + 0.9 + i * 0.07, 0.3, 0.05 - (i * 0.006), 'sine');
        });
        
        // Layer 7: Sustained harmonic pad
        playTone(ctx, 220, now + offset + 1.05, 1.5, 0.07, 'triangle');
        playTone(ctx, 330, now + offset + 1.1, 1.4, 0.05, 'triangle');
        playTone(ctx, 440, now + offset + 1.15, 1.3, 0.04, 'sine');
        
        // Layer 8: High shimmer
        const shimmer = [2640, 3520, 4400, 5280];
        shimmer.forEach((freq, i) => {
          playTone(ctx, freq, now + offset + 1.25 + i * 0.12, 0.2, 0.02 - (i * 0.004), 'sine');
        });
        
        // Layer 9: Echo tails
        playTone(ctx, 1320, now + offset + 1.65, 0.35, 0.035, 'sine');
        playTone(ctx, 1320, now + offset + 1.95, 0.3, 0.025, 'sine');
        playTone(ctx, 1320, now + offset + 2.25, 0.25, 0.015, 'sine');
        
        // Layer 10: Final resonant bell
        playTone(ctx, 880, now + offset + 2.05, 1.2, 0.06, 'sine');
        playTone(ctx, 1760, now + offset + 2.1, 1.0, 0.04, 'sine');
        break;
      }

      case 'cardReveal': {
        // Soft whoosh with subtle chime
        playTone(ctx, 400, time, 0.15, 0.06, 'sine');
        playTone(ctx, 600, time + 0.05, 0.15, 0.05, 'sine');
        playTone(ctx, 800, time + 0.08, 0.2, 0.04, 'triangle');
        break;
      }

      case 'pointsUp': {
        // Coin/points collection sound - quick ascending
        const baseFreq = 880;
        for (let i = 0; i < 8; i++) {
          playTone(ctx, baseFreq + (i * 80), time + (i * 0.04), 0.08, 0.06 - (i * 0.005), 'square');
        }
        // Final ding
        playTone(ctx, 1760, time + 0.35, 0.3, 0.08, 'sine');
        break;
      }

      case 'chime': {
        // Magical star chime
        playTone(ctx, 1200, time, 0.2, 0.08, 'sine');
        playTone(ctx, 1500, time + 0.05, 0.25, 0.07, 'sine');
        playTone(ctx, 1800, time + 0.1, 0.3, 0.06, 'sine');
        // Shimmer
        playTone(ctx, 2400, time + 0.15, 0.2, 0.03, 'sine');
        break;
      }

      case 'stamp': {
        // Heavy stamp impact with resonance
        // Impact thud
        playTone(ctx, 80, time, 0.15, 0.2, 'sine');
        playTone(ctx, 120, time, 0.1, 0.15, 'triangle');
        // Metallic ring
        playTone(ctx, 800, time + 0.02, 0.3, 0.08, 'sine');
        playTone(ctx, 1200, time + 0.02, 0.25, 0.05, 'sine');
        // Echo
        playTone(ctx, 600, time + 0.15, 0.2, 0.03, 'sine');
        break;
      }

      case 'powerup': {
        // Satisfying progress/power-up sweep
        const steps = 12;
        for (let i = 0; i < steps; i++) {
          const freq = 200 + (i * 60);
          playTone(ctx, freq, time + (i * 0.08), 0.12, 0.05, 'triangle');
        }
        // Final flourish
        playTone(ctx, 880, time + 0.9, 0.4, 0.1, 'sine');
        playTone(ctx, 1100, time + 0.95, 0.35, 0.08, 'sine');
        playTone(ctx, 1320, time + 1.0, 0.3, 0.06, 'sine');
        break;
      }

      case 'transitionImpact': {
        // Punchy handoff impact between HUD and Quest Complete
        const now = time;
        
        // Deep bass impact (felt in chest)
        playTone(ctx, 50, now, 0.2, 0.15, 'sine');
        playTone(ctx, 80, now, 0.15, 0.12, 'triangle');
        
        // Metallic ring (cinematic "arrival")
        playTone(ctx, 440, now, 0.08, 0.06, 'sine');
        playTone(ctx, 880, now + 0.02, 0.06, 0.04, 'sine');
        
        // High shimmer tail
        playTone(ctx, 2200, now + 0.03, 0.15, 0.03, 'sine');
        playTone(ctx, 3300, now + 0.04, 0.12, 0.02, 'sine');
        
        break;
      }
    }
  }, [playTone, playFrequencySweep]);

  const setEnabled = useCallback((enabled: boolean) => {
    isEnabledRef.current = enabled;
  }, []);

  return { playSound, setEnabled };
};